import React, { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Grid,
  TextField,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Chip,
  Alert,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from '@mui/material'
import {
  Calculate,
  Info,
  TrendingUp,
  Assessment
} from '@mui/icons-material'
import { useSnackbar } from 'notistack'
import medicalCodingService from '../../../services/medicalCodingService'

const DRGCalculator = ({ codingData = {}, onCalculate }) => {
  const { enqueueSnackbar } = useSnackbar()
  
  const [loading, setLoading] = useState(false)
  const [drgResult, setDrgResult] = useState(null)
  const [calculationDetails, setCalculationDetails] = useState(null)

  const handleCalculate = async () => {
    if (!codingData?.primaryDiagnosis?.icd10Code) {
      enqueueSnackbar('Primary diagnosis is required for DRG calculation', { variant: 'error' })
      return
    }

    setLoading(true)
    try {
      const response = await medicalCodingService.calculateDRG({
        primaryDiagnosis: codingData?.primaryDiagnosis,
        secondaryDiagnoses: codingData?.secondaryDiagnoses || [],
        procedures: codingData?.procedures || [],
        complications: codingData?.complications || [],
        comorbidities: codingData?.comorbidities || []
      })

      if (response.data.success) {
        setDrgResult(response.data.data)
        setCalculationDetails(response.data.details)
        enqueueSnackbar('DRG calculated successfully', { variant: 'success' })
      }
    } catch (error) {
      enqueueSnackbar('Failed to calculate DRG', { variant: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const handleAcceptDRG = () => {
    if (drgResult && onCalculate) {
      onCalculate(drgResult)
    }
  }

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 1: return 'success'
      case 2: return 'info'
      case 3: return 'warning'
      case 4: return 'error'
      default: return 'default'
    }
  }

  const getRiskColor = (risk) => {
    switch (risk) {
      case 1: return 'success'
      case 2: return 'info'
      case 3: return 'warning'
      case 4: return 'error'
      default: return 'default'
    }
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        DRG Calculator
      </Typography>
      
      <Typography variant="body2" color="text.secondary" paragraph>
        Calculate Diagnosis Related Group (DRG) and APR-DRG based on diagnoses and procedures
      </Typography>

      {/* Input Summary */}
      <Card sx={{ mb: 3 }}>
        <CardHeader title="Coding Summary" />
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom>
                Primary Diagnosis
              </Typography>
              {codingData?.primaryDiagnosis?.icd10Code ? (
                <Chip
                  label={`${codingData.primaryDiagnosis.icd10Code} - ${codingData.primaryDiagnosis.icd10Description}`}
                  color="primary"
                  variant="outlined"
                />
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No primary diagnosis
                </Typography>
              )}
            </Grid>
            
            <Grid item xs={12} md={3}>
              <Typography variant="subtitle2" gutterBottom>
                Secondary Diagnoses
              </Typography>
              <Typography variant="h6" color="primary">
                {codingData?.secondaryDiagnoses?.length || 0}
              </Typography>
            </Grid>
            
            <Grid item xs={12} md={3}>
              <Typography variant="subtitle2" gutterBottom>
                Procedures
              </Typography>
              <Typography variant="h6" color="primary">
                {codingData?.procedures?.length || 0}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Calculate Button */}
      <Box sx={{ textAlign: 'center', mb: 3 }}>
        <Button
          variant="contained"
          size="large"
          startIcon={<Calculate />}
          onClick={handleCalculate}
          disabled={loading || !codingData?.primaryDiagnosis?.icd10Code}
        >
          Calculate DRG
        </Button>
      </Box>

      {loading && <LinearProgress sx={{ mb: 3 }} />}

      {/* DRG Results */}
      {drgResult && (
        <Grid container spacing={3}>
          {/* MS-DRG */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardHeader 
                title="MS-DRG"
                avatar={<Assessment color="primary" />}
              />
              <CardContent>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="h4" color="primary" gutterBottom>
                    {drgResult.code}
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {drgResult.description}
                  </Typography>
                </Box>
                
                <Divider sx={{ my: 2 }} />
                
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Weight
                    </Typography>
                    <Typography variant="h6">
                      {drgResult.weight}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      MDC
                    </Typography>
                    <Typography variant="h6">
                      {drgResult.mdc}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* APR-DRG */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardHeader 
                title="APR-DRG"
                avatar={<TrendingUp color="secondary" />}
              />
              <CardContent>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="h4" color="secondary" gutterBottom>
                    {codingData?.aprDrg?.code || 'N/A'}
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {codingData?.aprDrg?.description || 'Not calculated'}
                  </Typography>
                </Box>
                
                <Divider sx={{ my: 2 }} />
                
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Severity of Illness
                    </Typography>
                    <Chip
                      label={`Level ${codingData?.aprDrg?.severityOfIllness || 1}`}
                      color={getSeverityColor(codingData?.aprDrg?.severityOfIllness)}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Risk of Mortality
                    </Typography>
                    <Chip
                      label={`Level ${codingData?.aprDrg?.riskOfMortality || 1}`}
                      color={getRiskColor(codingData?.aprDrg?.riskOfMortality)}
                      size="small"
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Calculation Details */}
          {calculationDetails && (
            <Grid item xs={12}>
              <Card>
                <CardHeader title="Calculation Details" />
                <CardContent>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    <Typography variant="body2">
                      DRG calculation is based on primary diagnosis, secondary diagnoses, procedures, and patient demographics.
                    </Typography>
                  </Alert>

                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Factor</TableCell>
                          <TableCell>Value</TableCell>
                          <TableCell>Impact</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow>
                          <TableCell>Primary Diagnosis</TableCell>
                          <TableCell>{codingData?.primaryDiagnosis?.icd10Code || 'N/A'}</TableCell>
                          <TableCell>Base DRG assignment</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Secondary Diagnoses</TableCell>
                          <TableCell>{codingData?.secondaryDiagnoses?.length || 0}</TableCell>
                          <TableCell>
                            {codingData?.secondaryDiagnoses?.length > 0 
                              ? `+${(codingData.secondaryDiagnoses.length * 0.05 * 100).toFixed(1)}% weight`
                              : 'No impact'
                            }
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Procedures</TableCell>
                          <TableCell>{codingData?.procedures?.length || 0}</TableCell>
                          <TableCell>
                            {codingData?.procedures?.length > 0 
                              ? `+${(codingData.procedures.length * 0.1 * 100).toFixed(1)}% weight`
                              : 'No impact'
                            }
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Action Buttons */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleAcceptDRG}
              >
                Accept DRG
              </Button>
              <Button
                variant="outlined"
                onClick={handleCalculate}
              >
                Recalculate
              </Button>
            </Box>
          </Grid>
        </Grid>
      )}

      {/* Information */}
      <Alert severity="info" sx={{ mt: 3 }}>
        <Typography variant="body2">
          <strong>Note:</strong> This is a simplified DRG calculation for demonstration purposes. 
          In production, use certified DRG grouper software for accurate results.
        </Typography>
      </Alert>
    </Box>
  )
}

export default DRGCalculator